import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { Box } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import type { FormikProps } from 'formik'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { mixpanelTrack } from '@homeviews/seo'
import { getFormChanges } from '@homeviews/utils'
import {
  useGetCompanyUserQuery,
  useCreateCompanyUserMutation,
  useUpdateCompanyUserMutation,
} from '@homeviews/store'
import {
  Typography,
  Button,
  TextField,
  Card,
  Spinner,
  Snackbar,
} from '@homeviews/shared-ui'
import type { ITheme } from '@homeviews/styles'
import type { User } from '@homeviews/models'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      width: '100%',
    },
    columns: {
      flexWrap: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        flexWrap: 'wrap',
      },
    },
    mainColumn: {},
    sideColumn: {
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    card: {
      width: '100%',
    },
    cardWrapper: {
      width: '100%',
    },
    sidebarInner: {
      width: 317,
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        width: 'initial',
      },
    },
    sidebarButton: {
      marginTop: theme.spacing(1.875),
    },
    summaryText: {
      color: theme.colors.grey490,
      marginTop: theme.spacing(1.875),
    },
    editMeta: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    editMetaLabel: {
      flex: ' 0 0 50%',
      paddingRight: theme.spacing(1),
      marginBottom: theme.spacing(0.625),
      fontWeight: 500,
      color: theme.colors.grey450,
    },
    editMetaValue: {
      flex: ' 0 0 50%',
      marginBottom: theme.spacing(0.625),
      color: theme.colors.grey800,
    },
    statusHeader: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    radios: {
      display: 'flex',
      '& .MuiFormControlLabel-root': {
        marginRight: theme.spacing(3.75),
      },
    },
  }),
)

interface IUserEditForm {
  first_name?: string
  last_name?: string
  email?: string
  job_title?: string
}

interface IProps {
  newUserForm?: boolean
  onSubmitCallback?: (data: Partial<User>) => void
}

const emptyFormState = {
  id: null,
  first_name: '',
  last_name: '',
  email: '',
  job_title: '',
}

const EditUserForm: FC<IProps> = ({ newUserForm, onSubmitCallback }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const { id: userID } = useParams<{ id: string }>()

  const [snackbarVisible, setSnackbarVisible] = useState<{
    open: boolean
    message: string
    status?: 'success' | 'error'
  }>({
    open: false,
    message: '',
    status: undefined,
  })

  const [createCompanyUser, { isLoading: isCreating }] =
    useCreateCompanyUserMutation()
  const [updateCompanyUser, { isLoading: isUpdating }] =
    useUpdateCompanyUserMutation()

  const {
    data: initialData,
    error,
    isFetching,
  } = useGetCompanyUserQuery(
    { id: userID ? parseInt(userID) : null },
    {
      skip: !userID,
    },
  )

  const user = initialData || null

  const [initialFormState, setInitialFormState] =
    useState<IUserEditForm>(emptyFormState)
  const [loading, setLoading] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')
  const [formSuccessMsg, setFormSuccessMsg] = useState<string>('')

  useEffect(() => {
    setInitialFormState({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      job_title: user?.job_title || '',
    })
  }, [user])

  const onSubmit = async (values: IUserEditForm) => {
    const payload = {
      data: {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        job_title: values.job_title,
      },
      id: userID,
    }

    if (newUserForm) {
      onSubmitCallback(payload.data)
      return
    }

    setLoading(true)

    try {
      const respData = await updateCompanyUser(payload).unwrap()

      const labelMap = {
        first_name: 'First Name',
        last_name: 'Last Name',
        email: 'Email',
        job_title: 'Job Title',
      }

      const formChanges = getFormChanges(initialData, payload.data, labelMap, [
        'global_permissions',
        'development_permissions',
      ])

      mixpanelTrack('COMPANY_USERS_EDIT', {
        'User Name': values?.first_name || values?.last_name,
        'Fields Edited': formChanges,
      })

      setFormSuccessMsg('Thank you.')

      setSnackbarVisible({
        open: true,
        message: 'Details updated',
        status: 'success',
      })
    } catch (err) {
      setSnackbarVisible({
        open: true,
        message: 'Oops, something went wrong',
        status: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSnackbarClose = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setSnackbarVisible({
      open: false,
      message: snackbarVisible?.message,
      status: snackbarVisible?.status,
    })
  }

  return (
    <div className={classes.root}>
      <Formik
        initialValues={initialFormState}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={Yup.object().shape({
          first_name: Yup.string().required('First name is required'),
          last_name: Yup.string().required('Surname is required'),
          email: Yup.string().email().required('Enter valid email'),
          job_title: Yup.string().required('Job title is required'),
        })}
      >
        {(props: FormikProps<IUserEditForm>) => {
          const {
            values,
            touched,
            errors,
            handleBlur,
            handleChange,
            setFieldValue,
          } = props

          const handleCancelClick = () => {
            history.push('/users')
          }

          if (isFetching) {
            return <Spinner />
          }

          return (
            <Form>
              <Grid container className={classes.columns} spacing={3}>
                <Grid item className={classes.mainColumn} xs={12}>
                  <Grid container spacing={3}>
                    <Grid item className={classes.cardWrapper}>
                      <Card
                        className={classes.card}
                        header={
                          <>
                            <Typography variant="h3">Details</Typography>
                            <Box marginTop={1}>
                              <Typography>Update profile details</Typography>
                            </Box>
                          </>
                        }
                      >
                        <div className="`l-fieldWrapper l-fieldWrapper--horizontal">
                          <div className="l-labelFieldWrapper">
                            <label>First name</label>
                          </div>
                          <TextField
                            value={values.first_name}
                            data-id="t-first_name"
                            id="first_name"
                            name="first_name"
                            error={errors.first_name && touched.first_name}
                            helperText={errors.first_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                          />
                        </div>

                        <div className="`l-fieldWrapper l-fieldWrapper--horizontal">
                          <div className="l-labelFieldWrapper">
                            <label>Surname</label>
                          </div>
                          <TextField
                            value={values.last_name}
                            data-id="t-last_name"
                            id="last_name"
                            name="last_name"
                            error={errors.last_name && touched.last_name}
                            helperText={errors.last_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                          />
                        </div>

                        <div className="`l-fieldWrapper l-fieldWrapper--horizontal">
                          <div className="l-labelFieldWrapper">
                            <label>Email</label>
                          </div>
                          <TextField
                            value={values.email}
                            data-id="t-email"
                            id="email"
                            name="email"
                            type="email"
                            error={errors.email && touched.email}
                            helperText={errors.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                          />
                        </div>

                        <div className="`l-fieldWrapper l-fieldWrapper--horizontal">
                          <div className="l-labelFieldWrapper">
                            <label>Job Title</label>
                          </div>
                          <TextField
                            value={values.job_title}
                            data-id="t-job_title"
                            id="job_title"
                            name="job_title"
                            error={
                              errors.job_title && touched.job_title
                                ? true
                                : false
                            }
                            helperText={errors.job_title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                          />
                        </div>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item className={classes.sideColumn}>
                  <Card
                    className={clsx(classes.card, classes.sidebarInner)}
                    header={<Typography variant="h3">Updates</Typography>}
                  >
                    <Typography className={classes.summaryText}>
                      Edit the details then click save to update the users
                      account.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      type="submit"
                      customColor="primary"
                      className={classes.sidebarButton}
                      isLoading={loading}
                    >
                      Save changes
                    </Button>

                    <Button
                      variant="outlined"
                      customVariant="outlinedPrimary"
                      fullWidth
                      onClick={handleCancelClick}
                      className={classes.sidebarButton}
                    >
                      Cancel without changes
                    </Button>
                  </Card>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
      <Snackbar
        open={snackbarVisible?.open}
        onClose={handleSnackbarClose}
        alertMessage={snackbarVisible?.message}
        status={snackbarVisible?.status}
      />
    </div>
  )
}

export default EditUserForm
